import { useState } from "react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";

import { ApiError } from "@/api/http";
import { accounting, type CorporateActionCreate } from "@/api/accounting";
import { Modal } from "@/components/Modal";
import { useToast } from "@/components/Toast";
import {
  Badge,
  Button,
  Card,
  CardBody,
  EmptyState,
  FormError,
  Input,
  Label,
  Select,
} from "@/components/ui";

function errMsg(e: unknown) {
  return e instanceof ApiError ? e.message.replace(/^\d+:\s*/, "") : String(e);
}

const KINDS = ["dividend", "split", "reverse_split", "stock_dividend", "merger", "spinoff"];

/**
 * Corporate actions — dividends, splits, mergers affecting held instruments.
 * Operators record actions manually; "Apply due" runs the same engine the
 * daemon uses to adjust positions for any action whose ex-date has passed.
 */
export function AdminCorporateActionsPage() {
  const list = useQuery({ queryKey: ["corp-actions"], queryFn: () => accounting.corporateActions() });
  const [creating, setCreating] = useState(false);
  const qc = useQueryClient();
  const toast = useToast();

  const applyDue = useMutation({
    mutationFn: () => accounting.applyDueCorporateActions(),
    onSuccess: (counts) => {
      const total = Object.values(counts).reduce((a, b) => a + b, 0);
      toast.push(`Applied due actions (${total} position adjustments)`, "success");
      qc.invalidateQueries({ queryKey: ["corp-actions"] });
    },
    onError: (e) => toast.push(errMsg(e), "error"),
  });

  return (
    <div className="space-y-3">
      <div className="flex items-center justify-between">
        <h1 className="text-base font-semibold text-term-fg">Corporate actions</h1>
        <div className="flex gap-2">
          <Button variant="ghost" onClick={() => applyDue.mutate()} loading={applyDue.isPending}>
            Apply due
          </Button>
          <Button onClick={() => setCreating(true)}>New action</Button>
        </div>
      </div>
      <Card>
        <CardBody className="p-0">
          {list.isLoading ? (
            <div className="p-3 text-sm text-term-mute">Loading…</div>
          ) : !list.data || list.data.length === 0 ? (
            <EmptyState title="No corporate actions" description="Record dividends, splits and mergers here." />
          ) : (
            <table className="w-full text-xs">
              <thead className="bg-term-elev text-term-mute">
                <tr>
                  <th className="px-3 py-1.5 text-left text-2xs uppercase tracking-wider">Instrument</th>
                  <th className="px-3 py-1.5 text-left text-2xs uppercase tracking-wider">Kind</th>
                  <th className="px-3 py-1.5 text-left text-2xs uppercase tracking-wider">Ex-date</th>
                  <th className="px-3 py-1.5 text-left text-2xs uppercase tracking-wider">Pay-date</th>
                  <th className="px-3 py-1.5 text-right text-2xs uppercase tracking-wider">Cash / ratio</th>
                  <th className="px-3 py-1.5 text-left text-2xs uppercase tracking-wider">Status</th>
                </tr>
              </thead>
              <tbody>
                {list.data.map((c) => (
                  <tr key={c.id} className="border-t border-term-border hover:bg-term-elev">
                    <td className="px-3 py-1.5 font-mono text-2xs text-term-dim">
                      {c.instrument_id ? c.instrument_id.slice(0, 8) : "—"}
                    </td>
                    <td className="px-3 py-1.5 text-term-fg">{c.kind}</td>
                    <td className="px-3 py-1.5 tnum text-term-dim">
                      {c.ex_date ? new Date(c.ex_date).toLocaleDateString() : "—"}
                    </td>
                    <td className="px-3 py-1.5 tnum text-term-dim">
                      {c.pay_date ? new Date(c.pay_date).toLocaleDateString() : "—"}
                    </td>
                    <td className="px-3 py-1.5 text-right tnum text-term-fg">
                      {c.cash_amount ? `${c.cash_amount} ${c.cash_currency ?? ""}` : c.ratio ?? "—"}
                    </td>
                    <td className="px-3 py-1.5">
                      <Badge tone={c.status === "applied" ? "good" : "neutral"}>{c.status}</Badge>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
        </CardBody>
      </Card>
      {creating && <ActionModal onClose={() => setCreating(false)} />}
    </div>
  );
}

function ActionModal({ onClose }: { onClose: () => void }) {
  const qc = useQueryClient();
  const toast = useToast();
  const [form, setForm] = useState<CorporateActionCreate>({
    instrument_id: "",
    kind: KINDS[0],
    ex_date: "",
    cash_currency: "USD",
  });
  const [err, setErr] = useState<string | null>(null);

  const create = useMutation({
    mutationFn: () =>
      accounting.createCorporateAction({
        ...form,
        ex_date: new Date(form.ex_date).toISOString(),
        pay_date: form.pay_date ? new Date(form.pay_date).toISOString() : null,
        cash_amount: form.cash_amount || null,
        ratio: form.ratio || null,
      }),
    onSuccess: () => {
      toast.push("Corporate action recorded", "success");
      qc.invalidateQueries({ queryKey: ["corp-actions"] });
      onClose();
    },
    onError: (e) => setErr(errMsg(e)),
  });

  return (
    <Modal open onClose={onClose} title="New corporate action">
      <form
        onSubmit={(e) => {
          e.preventDefault();
          setErr(null);
          create.mutate();
        }}
        className="space-y-3"
      >
        <div>
          <Label>Instrument id (UUID)</Label>
          <Input
            value={form.instrument_id}
            onChange={(e) => setForm({ ...form, instrument_id: e.target.value })}
          />
        </div>
        <div className="grid grid-cols-2 gap-2">
          <div>
            <Label>Kind</Label>
            <Select value={form.kind} onChange={(e) => setForm({ ...form, kind: e.target.value })}>
              {KINDS.map((k) => (
                <option key={k} value={k}>{k}</option>
              ))}
            </Select>
          </div>
          <div>
            <Label>Ex-date</Label>
            <Input type="date" value={form.ex_date} onChange={(e) => setForm({ ...form, ex_date: e.target.value })} />
          </div>
        </div>
        <div className="grid grid-cols-3 gap-2">
          <div>
            <Label>Pay-date</Label>
            <Input
              type="date"
              value={form.pay_date ?? ""}
              onChange={(e) => setForm({ ...form, pay_date: e.target.value || null })}
            />
          </div>
          <div>
            <Label>Cash amount</Label>
            <Input
              value={String(form.cash_amount ?? "")}
              onChange={(e) => setForm({ ...form, cash_amount: e.target.value || null })}
              placeholder="dividend"
            />
          </div>
          <div>
            <Label>Ratio</Label>
            <Input
              value={String(form.ratio ?? "")}
              onChange={(e) => setForm({ ...form, ratio: e.target.value || null })}
              placeholder="split e.g. 2"
            />
          </div>
        </div>
        <FormError>{err}</FormError>
        <div className="flex justify-end gap-2 pt-2">
          <Button type="button" variant="ghost" onClick={onClose}>
            Cancel
          </Button>
          <Button type="submit" loading={create.isPending}>
            Create
          </Button>
        </div>
      </form>
    </Modal>
  );
}
